<template>
  <v-btn
    :loading="isLoading"
    :disabled="isLoading || !hasSections"
    :ripple="false"
    color="secondary"
    @click="download"
  >
    <v-icon left>
      {{ icons.mdiFileExcel }}
    </v-icon>
    XLSXダウンロード
  </v-btn>
</template>

<script>
import { mdiFileExcel } from '@mdi/js'
import { ref, computed } from '@vue/composition-api'
import * as XLSX from 'xlsx/xlsx.mjs'
import ExpenseApi from '@/api/admin/Expense'
import BenefitApi from '@/api/admin/Benefit'
import TableSummaryApi from '@/api/admin/TableSummary'
import CashRegisterTransactionApi from '@/api/waiter/CashRegisterTransaction'
import CashRegisterTransactionSummaryApi from '@/api/waiter/CashRegisterTransactionSummary'
import useExpensesAndBenefitsDataTable from '@/views/composable/data-table/useExpensesAndBenefitsDataTable'
import useTableResultOverviewsDataTable from '@/views/composable/data-table/useTableResultOverviewsDataTable'
import useCashRegisterTransactionsDataTable from '@/views/composable/data-table/useCashRegisterTransactionsDataTable'
import useCashRegisterTransactionSummariesDataTable from '@/views/composable/data-table/useCashRegisterTransactionSummariesDataTable'
import useCompInit from '@/views/composable/useCompInit'

export default {
  props: {
    date: {
      type: String,
      required: true,
    },
    sections: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const { isLoading, initWith } = useCompInit()
    const hasSections = computed(() => props.sections.length > 0)

    const expenses = ref([])
    const benefits = ref([])
    const tableResultOverviews = ref([])
    const dynamicTableResultOverviewHeaders = ref([])
    const cashRegisterTransactions = ref([])
    const cashRegisterTransactionSummaries = ref([])

    const expensesAndBenefitsDataTable = useExpensesAndBenefitsDataTable({ expenses, benefits })
    const tableResultOverviewsDataTable = useTableResultOverviewsDataTable({ tableResultOverviews, dynamicTableResultOverviewHeaders })
    const {
      buildAllTransactionsXlsxData,
      buildDepositTransactionsXlsxData,
      buildWithdrawTransactionsXlsxData,
    } = useCashRegisterTransactionsDataTable(cashRegisterTransactions)
    const cashRegisterTxSummariesDataTable = useCashRegisterTransactionSummariesDataTable(cashRegisterTransactionSummaries)

    const buildSectionRows = section => {
      const row = { 営業日: props.date }
      const nameCount = {}
      section.metrics.forEach(metric => {
        nameCount[metric.name] = (nameCount[metric.name] || 0) + 1
      })
      const nameIndex = {}
      section.metrics.forEach(metric => {
        const key = nameCount[metric.name] > 1
          ? `${metric.name}(${(nameIndex[metric.name] = (nameIndex[metric.name] || 0) + 1)})`
          : metric.name
        row[key] = metric.total != null ? Number(metric.total) : ''
      })

      return [row]
    }

    const getExpenses = async () => {
      const res = await ExpenseApi.getExpenses({ date: props.date, interval: 0, endDate: '' })
      if (res?.status === 200) expenses.value = res.data.expenses.data
    }

    const getBenefits = async () => {
      const res = await BenefitApi.getBenefits({ date: props.date, interval: 0, endDate: '' })
      if (res?.status === 200) benefits.value = res.data.benefits.data
    }

    const getTableResultOverviews = async () => {
      const res = await TableSummaryApi.getOverviews({ date: props.date, interval: 0, endDate: '' })
      if (res?.status === 200) {
        tableResultOverviews.value = res.data.overviews
        dynamicTableResultOverviewHeaders.value = res.data.dynamicHeaders
      }
    }

    const getCashRegisterTransactions = async () => {
      const res = await CashRegisterTransactionApi.getCashRegisterTransactions({ date: props.date, interval: 0, endDate: '' })
      if (res?.status === 200) cashRegisterTransactions.value = res.data.cashRegisterTransactions.data
    }

    const getCashRegisterTransactionSummaries = async () => {
      const res = await CashRegisterTransactionSummaryApi.getCashRegisterTransactionSummaries({ date: props.date, interval: 0, endDate: '' })
      if (res?.status === 200) cashRegisterTransactionSummaries.value = res.data.cashRegisterTransactionSummaries
    }

    const writeXlsx = () => {
      const wb = XLSX.utils.book_new()

      props.sections.forEach(section => {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buildSectionRows(section)), section.label)
      })
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(expensesAndBenefitsDataTable.buildXlsxData()), '諸入費')
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tableResultOverviewsDataTable.buildXlsxData()), '卓概要(リスト)')
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(cashRegisterTxSummariesDataTable.buildXlsxData()), 'レジ金Tx(サマリ)')
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buildAllTransactionsXlsxData()), 'レジ金Tx(ALL)')
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buildDepositTransactionsXlsxData()), 'レジ金Tx(入金)')
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buildWithdrawTransactionsXlsxData()), 'レジ金Tx(出金)')

      XLSX.writeFile(wb, `日報β収支_${props.date}.xlsx`)
    }

    const download = async () => {
      await initWith([
        getExpenses(),
        getBenefits(),
        getTableResultOverviews(),
        getCashRegisterTransactions(),
        getCashRegisterTransactionSummaries(),
      ])
      writeXlsx()
    }

    return {
      isLoading,
      hasSections,
      download,
      icons: { mdiFileExcel },
    }
  },
}
</script>
